import { JSX, ReactNode, useEffect, useRef, useState } from 'react';
import { Controller, FieldPath, UseFormReturn } from 'react-hook-form';

import { Select, SelectValue } from 'components/Select';
import FormInputWrapper from 'components/input/FormInputWrapper';
import { useLoadContext } from 'hooks/useLoadContext';
import useTMSContext from 'hooks/useTMSContext';
import { NormalizedLoad } from 'types/Load';
import { getFieldAttribute } from 'types/LoadAttributes';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';
import { TMS, integrationNameMap } from 'types/enums/Integrations';

import { LoadBuildingTextInput } from './LoadBuildingTextInput';

// The rate types the grid knows how to turn into rating units. A TMS offers the subset it
// supports through rateTypeOptions and labels them in its own words.
export enum RateType {
  Flat = 'Flat',
  Distance = 'Distance',
  CWT = 'CWT',
  Tons = 'Tons',
}

export enum RateParty {
  Customer = 'customer',
  Carrier = 'carrier',
}

export type RateTypeOption = {
  value: RateType | string;
  label: string;
  // The TMS's words for this type's rate and units (PLS: "Rate per Mile", "Miles"). Unset, the
  // grid uses McLeod's "Line Haul Rate" and "Rating Units".
  rateLabel?: string;
  unitsLabel?: string;
  // The rate is the line haul in dollars (PLS's FLAT_RATE line), so the grid shows only the rate:
  // one rating unit and a charge equal to the rate would say the same thing twice.
  rateIsLineHaul?: boolean;
};

interface RateGridProps {
  party: RateParty;
  formMethods: UseFormReturn<NormalizedLoad>;
  rateTypeOptions: RateTypeOption[];
  // The load's weight in lbs when the TMS keeps it on its items rather than in
  // specifications.totalWeight (PLS); CWT and Tons units come from it.
  totalWeightLbs?: number;
  // Replaces the read-only units field when the rep enters the units, e.g. PLS's trip miles.
  unitsInput?: ReactNode;
}

// Rate type, rating units, line haul rate and line haul charge for one side of a load. Units
// and charge are derived from the rate type, the load's weight or distance, and the rate.
export function RateGrid({
  party,
  formMethods,
  rateTypeOptions,
  totalWeightLbs,
  unitsInput,
}: RateGridProps): JSX.Element {
  const { control, setValue, clearErrors, watch, setError } = formMethods;

  const { fieldAttributes } = useLoadContext();
  const { tmsName } = useTMSContext();

  // Mcleod calculates distance after creating load
  const [isRatingTypeDistance, setIsRateTypeDistance] = useState(false);

  // Watch relevant fields from the form
  const watchedSpecsWeight = watch('specifications.totalWeight.val');
  const watchedWeight = totalWeightLbs ?? watchedSpecsWeight;
  const watchedDistance = watch('specifications.totalDistance.val');

  const watchedRateType = watch(`rateData.${party}RateType`);
  const watchedRate = watch(`rateData.${party}LineHaulRate`);
  // The rate type the units were last computed for, so a switch to Distance drops the units the
  // previous type set instead of rating the miles as 1.
  const unitsRateTypeRef = useRef<Maybe<string>>(null);

  const unitsLabelFor = (rateType: string) =>
    rateTypeOptions.find((option) => option.value === rateType)?.unitsLabel;

  function calculateInputs(
    party: RateParty,
    rateType: string,
    weight: MaybeUndef<number>,
    distance: MaybeUndef<number>,
    rate: Maybe<number>
  ) {
    // Get current value to preserve it if calculation fails
    const currentRatingUnits = watch(
      `rateData.${party}RateNumUnits` as FieldPath<NormalizedLoad>
    ) as Maybe<number>;

    let ratingUnits: Maybe<number> = 1;
    setIsRateTypeDistance(false);
    clearErrors('specifications.totalWeight.val');
    clearErrors(
      `rateData.${party}LineHaulCharge.val` as FieldPath<NormalizedLoad>
    );

    switch (rateType) {
      case RateType.Flat:
        ratingUnits = 1; // Flat rate always has 1 unit
        break;

      case RateType.Distance: {
        setIsRateTypeDistance(true);
        // *After* load is created, Mcleod calculates distance so we can calculate rating units when user makes updates
        const isSwitchFromOtherType =
          Boolean(unitsRateTypeRef.current) &&
          unitsRateTypeRef.current !== rateType;
        if (distance) {
          ratingUnits = distance;
        } else if (unitsInput || unitsLabelFor(rateType)) {
          // PLS's units are the trip miles (its sync stores 1), so with no miles there are no
          // units and no charge. Clearing the miles clears both.
          ratingUnits = null;
        } else {
          ratingUnits =
            !isSwitchFromOtherType &&
            currentRatingUnits &&
            currentRatingUnits > 0
              ? currentRatingUnits
              : null;
        }
        break;
      }

      case RateType.CWT: // Hundredweight = 100 pounds
        if (weight) {
          ratingUnits = weight / 100;
        } else {
          setError('specifications.totalWeight.val', {
            message: 'Required for weight-based rate',
          });
          setError(
            `rateData.${party}LineHaulCharge.val` as FieldPath<NormalizedLoad>,
            {
              message: 'Missing weight',
            }
          );
        }
        break;

      case RateType.Tons: // Tons = 2000 pounds
        if (weight) {
          ratingUnits = weight / 2000;
        } else {
          setError('specifications.totalWeight.val', {
            message: 'Required for weight-based rate',
          });
          setError(
            `rateData.${party}LineHaulCharge.val` as FieldPath<NormalizedLoad>,
            {
              message: 'Missing weight',
            }
          );
        }
        break;

      default:
        ratingUnits =
          currentRatingUnits && currentRatingUnits > 0
            ? currentRatingUnits
            : null;
        break;
    }

    unitsRateTypeRef.current = rateType;

    // Set calculated values in the form
    setValue(
      `rateData.${party}RateNumUnits` as FieldPath<NormalizedLoad>,
      ratingUnits
    );

    if (ratingUnits && rate) {
      setValue(
        `rateData.${party}LineHaulCharge.val` as FieldPath<NormalizedLoad>,
        ratingUnits * rate
      );
    } else {
      setValue(
        `rateData.${party}LineHaulCharge.val` as FieldPath<NormalizedLoad>,
        null
      );
    }
  }

  useEffect(() => {
    calculateInputs(
      party,
      watchedRateType,
      watchedWeight,
      watchedDistance,
      watchedRate
    );
    // calculateInputs is redefined each render; key on rate inputs instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [party, watchedDistance, watchedRate, watchedRateType, watchedWeight]);

  const selectedOption = rateTypeOptions.find(
    (option) => option.value === watchedRateType
  );
  const tmsTBD = isRatingTypeDistance
    ? `${integrationNameMap[tmsName as TMS]} TBD`
    : undefined;

  // A flat rate is the whole line haul, so units and a charge would only repeat it.
  const showsLineHaulOnly = Boolean(selectedOption?.rateIsLineHaul);

  // Rating Units (read-only unless the TMS lets the rep enter them)
  const unitsField = unitsInput ?? (
    <div className='col-span-2'>
      <LoadBuildingTextInput
        name={`rateData.${party}RateNumUnits` as FieldPath<NormalizedLoad>}
        label={selectedOption?.unitsLabel ?? 'Rating Units'}
        // NOTE: Use disabled instead of readOnly so string placeholder is shown
        options={{ valueAsNumber: true, disabled: true }}
        placeholder={tmsTBD}
      />
    </div>
  );

  // Line Haul Charge = Rate * Units
  const chargeField = (
    <div className='col-span-2'>
      <LoadBuildingTextInput
        name={
          `rateData.${party}LineHaulCharge.val` as FieldPath<NormalizedLoad>
        }
        label='Line Haul Charge'
        placeholder={tmsTBD}
        // NOTE: Use disabled instead of readOnly so string placeholder is shown
        options={{ valueAsNumber: true, disabled: true }}
      />
    </div>
  );

  const rateInput = (
    <div className='col-span-2'>
      <LoadBuildingTextInput
        name={`rateData.${party}LineHaulRate` as FieldPath<NormalizedLoad>}
        label={selectedOption?.rateLabel ?? 'Line Haul Rate'}
        options={{ valueAsNumber: true }}
      />
    </div>
  );

  return (
    <>
      {/* Rate Type */}
      <div className='col-span-2'>
        <FormInputWrapper name={`rateData.${party}RateType`} label='Rate Type'>
          <Controller
            name={`rateData.${party}RateType` as FieldPath<NormalizedLoad>}
            control={control}
            render={({ field }) => (
              <div className='text-neutral-500'>
                <Select
                  showSearch
                  // FormInputWrapper spaces the label the way it does for the text inputs beside
                  // this one, so drop the select's own top margin to line the boxes up.
                  className='h-8 mt-0'
                  placeholder={'Choose'}
                  optionFilterProp='children'
                  onChange={(nextType) => {
                    // PLS means a different amount under each rate type (flat line haul, rate per
                    // mile, rate per CWT). Carrying $2.50 over from a flat line haul would turn it
                    // into $2.50 a mile, so the rep re-enters the rate.
                    if (
                      rateTypeOptions.some((option) => option.rateLabel) &&
                      field.value &&
                      field.value !== nextType
                    ) {
                      setValue(
                        `rateData.${party}LineHaulRate` as FieldPath<NormalizedLoad>,
                        null
                      );
                    }
                    field.onChange(nextType);
                  }}
                  value={
                    field.value === null ||
                    field.value === undefined ||
                    field.value === true ||
                    field.value === false ||
                    field.value instanceof Date ||
                    (typeof field.value === 'object' &&
                      field.value !== null &&
                      !Array.isArray(field.value) &&
                      !('value' in field.value && 'label' in field.value))
                      ? undefined
                      : (field.value as SelectValue)
                  }
                  disabled={
                    getFieldAttribute(
                      fieldAttributes,
                      `rateData.${party}RateType`
                    )?.isReadOnly
                  }
                  options={rateTypeOptions}
                />
              </div>
            )}
          />
        </FormInputWrapper>
      </div>
      {/* The rate input stays in the same slot for every type. Remounting it registers its empty
          value as NaN, which the box then shows. */}
      {!showsLineHaulOnly && unitsField}
      {rateInput}
      {!showsLineHaulOnly && chargeField}
    </>
  );
}
